/**
 * Tab Stop Navigation Extension
 * Enables Tab/Shift+Tab navigation through snippet placeholders
 */

import { Extension, StateField, StateEffect } from '@codemirror/state'
import { EditorView, Decoration, DecorationSet, WidgetType, keymap } from '@codemirror/view'
import { acceptCompletion } from '@codemirror/autocomplete'
import { indentMore, indentLess } from '@codemirror/commands'
import { logDebug } from '@utils/logger'

export interface TabStop {
  id: string
  from: number
  to: number
  placeholder?: string
  isFinal: boolean
}

interface TabStopState {
  stops: TabStop[]
  currentIndex: number
}

// Effect to set tab stops
const setTabStopsEffect = StateEffect.define<TabStop[]>()

// Effect to set current index
const setCurrentIndexEffect = StateEffect.define<number>()

// Effect to clear tab stops
const clearTabStopsEffect = StateEffect.define<boolean>()

// Widget to show placeholder text for empty tab stops
class TabStopPlaceholderWidget extends WidgetType {
  constructor(private placeholder: string) {
    super()
  }
  
  toDOM() {
    const span = document.createElement('span')
    span.className = 'cm-tabstop-placeholder'
    span.textContent = this.placeholder
    return span
  }
  
  ignoreEvent() {
    return false
  }
}

// State field to track active tab stops
const tabStopsField = StateField.define<TabStopState>({
  create() {
    return { stops: [], currentIndex: -1 }
  },
  update(value, tr) {
    let newValue = { ...value }
    
    // Apply effects first
    for (const effect of tr.effects) {
      if (effect.is(setTabStopsEffect)) {
        const stops = effect.value
        newValue = {
          stops: stops.map((stop, idx) => ({
            ...stop,
            id: stop.id || `tabstop-${idx}`
          })),
          currentIndex: stops.length > 0 ? 0 : -1
        }
      } else if (effect.is(setCurrentIndexEffect)) {
        newValue = {
          ...newValue,
          currentIndex: effect.value
        }
      } else if (effect.is(clearTabStopsEffect)) {
        return { stops: [], currentIndex: -1 }
      }
    }
    
    // Adjust positions for document changes
    if (tr.docChanged && newValue.stops.length > 0) {
      const newStops = newValue.stops.map(stop => {
        const newFrom = tr.changes.mapPos(stop.from, 1)
        const newTo = tr.changes.mapPos(stop.to, -1)
        return {
          ...stop,
          from: newFrom,
          to: newTo
        }
      }).filter(stop => stop.from >= 0 && stop.to <= tr.newDoc.length && stop.from <= stop.to)
      
      newValue = {
        stops: newStops,
        currentIndex: newStops.length > 0 
          ? Math.min(newValue.currentIndex, newStops.length - 1) 
          : -1
      }
    }
    
    // Clear tab stops if user types outside of them or deletes content
    if (tr.docChanged && newValue.stops.length > 0 && newValue.currentIndex >= 0) {
      const selection = tr.newSelection.main
      
      // Check if selection is within any tab stop
      const isInAnyTabStop = newValue.stops.some(stop => 
        selection.from >= stop.from && selection.to <= stop.to
      )
      
      // If cursor moved outside all tab stops, clear them
      if (!isInAnyTabStop) {
        // But only if it's a significant change (not just navigation)
        const hasSignificantChange = tr.effects.length === 0 || 
          !tr.effects.some(e => e.is(setCurrentIndexEffect))
        
        if (hasSignificantChange) {
          return { stops: [], currentIndex: -1 }
        }
      }
    }
    
    return newValue
  },
  provide: f => EditorView.decorations.from(f, (state) => {
      const { stops, currentIndex } = state
      if (stops.length === 0) return Decoration.none
      
      const decorations: Decoration[] = []
      
      stops.forEach((stop, idx) => {
        const isActive = idx === currentIndex
        
        // For empty tab stops, use a widget to show placeholder
        if (stop.to === stop.from) {
          if (isActive) {
            decorations.push(
              Decoration.widget({
                widget: new TabStopPlaceholderWidget(stop.placeholder || '...'),
                side: 1
              }).range(stop.from)
            )
          }
        } else {
          // For non-empty tab stops, use mark decoration
          const className = isActive 
            ? 'cm-tabstop-active' 
            : 'cm-tabstop'
          
          decorations.push(
            Decoration.mark({
              class: className,
              attributes: {
                'data-tabstop-id': stop.id,
                'data-tabstop-index': idx.toString(),
                'title': stop.placeholder || `Tab stop ${idx + 1}`
              }
            }).range(stop.from, stop.to)
          )
        }
      })
      
      return Decoration.set(decorations)
    })
})

/**
 * Navigate to next tab stop
 */
function navigateToNextTabStop(view: EditorView): boolean {
  const state = view.state.field(tabStopsField)
  if (state.stops.length === 0 || state.currentIndex < 0) return false
  
  const nextIndex = (state.currentIndex + 1) % state.stops.length
  const nextStop = state.stops[nextIndex]
  
  if (nextStop) {
    view.dispatch({
      effects: [
        setCurrentIndexEffect.of(nextIndex)
      ],
      selection: {
        anchor: nextStop.from,
        head: nextStop.to
      },
      scrollIntoView: true
    })
    
    return true
  }
  
  return false
}

/**
 * Navigate to previous tab stop
 */
function navigateToPreviousTabStop(view: EditorView): boolean {
  const state = view.state.field(tabStopsField)
  if (state.stops.length === 0) return false
  
  const prevIndex = state.currentIndex <= 0 
    ? state.stops.length - 1 
    : state.currentIndex - 1
  const prevStop = state.stops[prevIndex]
  
  if (prevStop) {
    view.dispatch({
      effects: [
        setCurrentIndexEffect.of(prevIndex)
      ],
      selection: {
        anchor: prevStop.from,
        head: prevStop.to
      },
      scrollIntoView: true
    })
    
    return true
  }
  
  return false
}

/**
 * Set tab stops after snippet insertion
 */
export function setTabStops(view: EditorView, stops: TabStop[]): void {
  if (stops.length === 0) return
  
  view.dispatch({
    effects: setTabStopsEffect.of(stops)
  })
  
  // Navigate to first tab stop
  if (stops.length > 0) {
    const firstStop = stops[0]
    view.dispatch({
      effects: setCurrentIndexEffect.of(0),
      selection: {
        anchor: firstStop.from,
        head: firstStop.to
      },
      scrollIntoView: true
    })
  }
}

/**
 * Clear tab stops
 */
export function clearTabStops(view: EditorView): void {
  view.dispatch({
    effects: clearTabStopsEffect.of(true)
  })
}

/**
 * Tab stop navigation keymap
 */
const tabStopKeymap = keymap.of([
  {
    key: 'Tab',
    preventDefault: true,
    run: (view: EditorView) => {
      const state = view.state.field(tabStopsField)
      
      // Priority 1: If we have active tab stops, navigate to next
      if (state.stops.length > 0 && state.currentIndex >= 0) {
        return navigateToNextTabStop(view)
      }
      
      // Priority 2: Check if autocomplete is active using DOM
      // CodeMirror doesn't export completionState, so we use DOM checks
      const autocompletePanels = view.dom.querySelectorAll('.cm-tooltip-autocomplete')
      
      for (const panel of Array.from(autocompletePanels)) {
        const style = window.getComputedStyle(panel)
        const isVisible = style.display !== 'none' && 
                         style.visibility !== 'hidden' &&
                         parseFloat(style.opacity) > 0
        
        if (isVisible) {
          const accepted = acceptCompletion(view)
          if (accepted) {
            return true
          }
        }
      }
      
      // Priority 3: Default - indent
      return indentMore(view)
    }
  },
  {
    key: 'Shift-Tab',
    preventDefault: true,
    run: (view: EditorView) => {
      const state = view.state.field(tabStopsField)
      
      // If we have active tab stops, navigate to previous
      if (state.stops.length > 0 && state.currentIndex >= 0) {
        return navigateToPreviousTabStop(view)
      }
      
      // Otherwise, indent less
      return indentLess(view)
    }
  },
  {
    key: 'Escape',
    run: (view: EditorView) => {
      const state = view.state.field(tabStopsField)
      
      // Clear tab stops on Escape
      if (state.stops.length > 0) {
        clearTabStops(view)
        return true
      }
      
      return false
    }
  }
])

/**
 * Tab stop navigation extension
 */
export function tabStopNavigation(): Extension {
  return [
    tabStopsField,
    tabStopKeymap,
    EditorView.theme({
      '.cm-tabstop': {
        backgroundColor: 'rgba(100, 150, 255, 0.08)',
        borderRadius: '3px',
        outline: '1px solid rgba(100, 150, 255, 0.25)',
        padding: '1px 3px',
        margin: '0 1px',
        transition: 'all 0.15s ease'
      },
      '.cm-tabstop-active': {
        backgroundColor: 'rgba(100, 150, 255, 0.2)',
        outline: '2px solid rgba(100, 150, 255, 0.6)',
        fontWeight: '500',
        padding: '1px 3px',
        margin: '0 1px',
        boxShadow: '0 0 4px rgba(100, 150, 255, 0.3)',
        transition: 'all 0.15s ease'
      },
      '.cm-tabstop-placeholder': {
        fontSize: '0.9em',
        fontStyle: 'italic',
        color: '#4a90e2'
      },
      // Dark mode support
      '.dark .cm-tabstop': {
        backgroundColor: 'rgba(100, 150, 255, 0.15)',
        outline: '1px solid rgba(100, 150, 255, 0.4)'
      },
      '.dark .cm-tabstop-active': {
        backgroundColor: 'rgba(100, 150, 255, 0.3)',
        outline: '2px solid rgba(100, 150, 255, 0.7)',
        boxShadow: '0 0 6px rgba(100, 150, 255, 0.4)'
      }
    })
  ]
}
